import { ApolloError, gql } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import Modal from '../../../../common/src/components/Modal'
import TypeaheadBox from '../../../../common/src/components/TypeaheadBox'
import { IntroText, EllipsisDiv, Spacer, BodyTextSpan } from '../../../../common/src/components/core'
import { Colors } from '../../../../common/src/constants'
import { computeErrorMessage, handleError } from '../../../../common/src/data/errors'
import memoize from '../../../../common/src/memoize'
import { QuickActionResult, FetchQuickActionsQuery } from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { Fragments } from '../../graphql/fragments'

const quickActionsQuery = gql`
  query FetchQuickActions($threadId: Int!, $prefix: String!, $maxResults: Int!, $resetCache: Boolean!) {
    quickActions(threadId: $threadId, prefix: $prefix, maxResults: $maxResults, resetCache: $resetCache) {
      ...QuickActionResult
    }
  }

  fragment QuickActionResult on QuickActionResult {
    displayName
    snippet
    body
    hasError
    action {
      ...ActionToExec
    }
    composerJson
  }
  ${Fragments.actionToExec}
`

interface ShowAllRow {
  shortcut: 'show-all'
  displayName: string
  snippet: string
  hasError: boolean
}

type RowType = FetchQuickActionsQuery['quickActions'][0] | ShowAllRow

interface QuickActionsBoxProps {
  innerRef: React.RefObject<HTMLInputElement>
  threadId: number
  onChoose: (result: FetchQuickActionsQuery['quickActions'][0]) => void
}

interface QuickActionsBoxState {
  showAll: boolean
  resetCache: boolean
  threadId: number
}

export default class QuickActionsBox extends React.PureComponent<QuickActionsBoxProps, QuickActionsBoxState> {
  constructor(props: QuickActionsBoxProps) {
    super(props)
    this.state = { showAll: false, resetCache: true, threadId: props.threadId }
  }

  static getDerivedStateFromProps(props: QuickActionsBoxProps, state: QuickActionsBoxState) {
    return {
      showAll: state.showAll,
      resetCache: state.resetCache || props.threadId !== state.threadId,
      threadId: props.threadId,
    }
  }

  render() {
    if (this.state.showAll) {
      return (
        <Modal title="Quick actions" width={960} onClose={this.onCloseModal}>
          <AllQuickActions threadId={this.props.threadId} onChoose={this.onChoose} />
        </Modal>
      )
    }

    return (
      <TypeaheadBox<RowType>
        innerRef={this.props.innerRef}
        inputElementId={'quick-actions-typeahead'}
        loadFunc={this.loadBound}
        rowFunc={QuickActionRow}
        onChoose={this.onChoose}
        clearOnBlur={true}
        placeholder="Quick action. Try typing 'mon', 'mon 4pm', or 'cats'..."
        showResultsWhenEmpty
        debounceResultLoading
      />
    )
  }

  /**
   * given a string prefix, searches for matching quick actions
   */
  private loadBound = (prefix: string): Promise<RowType[]> => {
    const { threadId } = this.props
    const maxResults = 6

    return queryQuickActions(threadId, prefix, maxResults, this.state.resetCache)
      .then(({ data }) => {
        const ret = (data?.quickActions.slice() as RowType[]) || []
        ret.push({
          shortcut: 'show-all',
          displayName: 'Show all',
          snippet: '',
          hasError: false,
        })
        this.setState({ resetCache: threadId !== this.props.threadId })
        return ret
      })
      .catch((e: ApolloError) => {
        const errMsg = computeErrorMessage(e)
        return [
          {
            __typename: 'QuickActionResult',
            hasError: true,
            displayName: 'Error',
            snippet: errMsg || '',
            body: null,
            action: null,
            composerJson: null,
          },
        ]
      })
  }

  private onChoose = (v: RowType) => {
    const { onChoose } = this.props
    if ((v as ShowAllRow).shortcut) {
      this.setState({ showAll: true })
    } else {
      this.setState({ showAll: false })
      onChoose(v as QuickActionResult)
    }
  }

  private onCloseModal = () => {
    this.setState({ showAll: false })
  }
}

interface AllQuickActionsProps {
  threadId: number
  onChoose: (action: FetchQuickActionsQuery['quickActions'][0]) => void
}

class AllQuickActions extends React.PureComponent<
  AllQuickActionsProps,
  { results?: FetchQuickActionsQuery['quickActions'] }
> {
  private handlers = {} as { [key: string]: any }

  constructor(props: AllQuickActionsProps) {
    super(props)
    this.state = {}
  }

  componentDidMount() {
    const { threadId } = this.props
    queryQuickActions(threadId, '', 100, false)
      .then(({ data }) => this.setState({ results: data?.quickActions }))
      .catch(handleError)
  }

  render() {
    const { results } = this.state
    if (!results) {
      return <IntroText>Loading…</IntroText>
    }

    return (
      <AllQuickActionsWrap>
        {results.map((res, i) => (
          <QuickActionPellet key={i} onClick={memoize(this.handlers, i, () => () => this.props.onChoose(res))}>
            <QuickActionRow {...res} />
          </QuickActionPellet>
        ))}
      </AllQuickActionsWrap>
    )
  }
}

const AllQuickActionsWrap = styled.div`
  margin: 0 -12px;
`

const QuickActionPellet = styled.div`
  display: inline-block;
  width: 912px;
  padding: 8px 12px;
  cursor: pointer;
  &:hover {
    background-color: ${Colors.black};
  }
`

function queryQuickActions(threadId: number, prefix: string, maxResults: number, resetCache: boolean) {
  return apolloWebClient.query<FetchQuickActionsQuery>({
    query: quickActionsQuery,
    variables: { threadId, prefix, maxResults, resetCache },
    fetchPolicy: 'network-only',
  })
}

function QuickActionRow(props: RowType) {
  const color = props.hasError ? 'lemon' : 'silver'
  if ((props as ShowAllRow).shortcut) {
    return <IntroText color="silver">Show all</IntroText>
  }
  return (
    <IntroText color="snow">
      <EllipsisDiv>
        {props.displayName}
        <Spacer width={8} />
        <BodyTextSpan color={color}>{props.snippet}</BodyTextSpan>
      </EllipsisDiv>
    </IntroText>
  )
}
